// ============================================
// Seed Templates: Acquisition Data Room
// Category: acquisition-dataroom
// Templates: 7
// ============================================

import type { DocTemplate } from '../../types/docops';

export const SEED_TEMPLATES_ACQUISITION: DocTemplate[] = [
  // ── 11.1 Acquisition Pitch Deck ──
  {
    id: '11.1-acquisition-deck',
    categoryId: 'acquisition-dataroom',
    track: 'acquisition',
    title: 'Acquisition Pitch Deck',
    description:
      'Investor/acquirer presentation covering market, technology, team, and financials.',
    templateContent: '',
    requiredVariables: ['brandName', 'brandEntity', 'marketVertical', 'ceoName'],
    generationPrompt:
      'Generate outline for Acquisition Pitch Deck for {{brandName}} ({{brandEntity}}). Sections: 1) Problem (fragmented health optimization, consumers lack actionable lab insights), 2) Solution (AI-powered bloodwork analysis + supplement protocols + provider access), 3) Market ({{marketVertical}} TAM/SAM/SOM), 4) Technology (hybrid AI engine, clinical tier assessment, automated protocol builder), 5) Business Model (freemium + supplement revenue + consultation fees), 6) Traction (user metrics, revenue, retention), 7) Team ({{ceoName}}, Medical Director, advisors), 8) Competitive Advantages (multi-brand platform architecture, IP trust structure, regulatory compliance built-in), 9) Financial Projections, 10) Ask/Use of Funds. Format: slide-by-slide outline with key talking points.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 15,
    sortOrder: 1,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 11.2 Financial Model Summary ──
  {
    id: '11.2-financial-model',
    categoryId: 'acquisition-dataroom',
    track: 'acquisition',
    title: 'Financial Model Summary',
    description:
      'Revenue projections, unit economics, CAC/LTV analysis, growth scenarios.',
    templateContent: '',
    requiredVariables: ['brandName', 'consultationFee', 'supplementMarginPercent'],
    generationPrompt:
      'Generate Financial Model narrative summary for {{brandName}}. Revenue streams: 1) Supplement sales (margin {{supplementMarginPercent}}), 2) Provider consultations (${{consultationFee}} per visit), 3) Subscription tiers (free/tier1/tier2/tier3). Unit economics: CAC, LTV, payback period. Growth scenarios: conservative/base/aggressive for 12/24/36 months. Key assumptions and sensitivity analysis. Format: narrative document with projected tables (not spreadsheet — summary for data room).',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 10,
    sortOrder: 2,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 11.3 Data Room Index ──
  {
    id: '11.3-data-room-index',
    categoryId: 'acquisition-dataroom',
    track: 'acquisition',
    title: 'Data Room Index',
    description:
      'Organized directory structure for virtual data room with all diligence documents.',
    templateContent: '',
    requiredVariables: ['brandName'],
    generationPrompt:
      'Generate Data Room Index for {{brandName}} acquisition due diligence. Folders: 1) Corporate (formation docs, operating agreement, resolutions), 2) IP (trust agreement, assignment, license, asset schedule, valuation), 3) Financial (model, tax returns, bank statements), 4) Legal (contracts, litigation, regulatory), 5) Technology (architecture docs, security audit, code repository access), 6) Clinical (protocols, formulary, provider agreements), 7) Compliance (HIPAA, privacy, consent, data ops), 8) Team (org chart, key agreements, equity grants), 9) Insurance, 10) Customer/Revenue (metrics dashboard, retention data). Format: hierarchical directory listing with document names and status.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 3,
    sortOrder: 3,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 11.4 Complete IP Asset Inventory ──
  {
    id: '11.4-ip-asset-inventory',
    categoryId: 'acquisition-dataroom',
    track: 'acquisition',
    title: 'Complete IP Asset Inventory',
    description:
      'Comprehensive inventory of all intellectual property held by the trust.',
    templateContent: '',
    requiredVariables: ['brandName', 'trustName'],
    generationPrompt:
      'Generate detailed IP Asset Inventory for {{brandName}} (held by {{trustName}}). Expand on IP Asset Schedule with: registration status (trademark filed/pending/registered, domain registered, copyright filed), valuation estimate per asset category, maintenance requirements and costs, third-party IP dependencies (open source licenses, API terms), freedom to operate analysis summary. Format: detailed inventory table with columns for asset, type, status, valuation, maintenance, dependencies.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 8,
    sortOrder: 4,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 11.5 Capitalization Table ──
  {
    id: '11.5-cap-table',
    categoryId: 'acquisition-dataroom',
    track: 'acquisition',
    title: 'Capitalization Table',
    description:
      'Current equity positions, option pool, and ownership percentages.',
    templateContent: '',
    requiredVariables: ['brandEntity', 'ceoName'],
    generationPrompt:
      'Generate Cap Table template for {{brandEntity}}. Structure: Managing Member ({{ceoName}}): [X]%, Medical Director: [X]%, Team Option Pool: [X]%, Reserved/Future: [X]%. Include: fully diluted calculations, vesting schedules, any convertible notes or SAFEs. Format: table template with fillable percentages.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 2,
    sortOrder: 5,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 11.6 Disclosure Schedule ──
  {
    id: '11.6-disclosure-schedule',
    categoryId: 'acquisition-dataroom',
    track: 'acquisition',
    title: 'Disclosure Schedule',
    description:
      'Known risks, pending matters, and material disclosures for acquisition diligence.',
    templateContent: '',
    requiredVariables: ['brandEntity'],
    generationPrompt:
      'Generate Disclosure Schedule template for {{brandEntity}}. Sections: 1) Pending/threatened litigation (none expected), 2) Regulatory matters (MHMDA compliance in progress, no enforcement actions), 3) Material contracts (list key vendor agreements), 4) IP matters (all IP assigned to trust, no infringement claims), 5) Tax matters (current filings), 6) Environmental matters (N/A — software company), 7) Employee matters (contractor classifications), 8) Insurance claims, 9) Known risks (regulatory landscape evolving, AI regulation pending). Format: schedule with sections and fillable details.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 5,
    sortOrder: 6,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 11.7 Letter of Intent Template ──
  {
    id: '11.7-loi-template',
    categoryId: 'acquisition-dataroom',
    track: 'acquisition',
    title: 'Letter of Intent Template',
    description:
      'Template for structuring incoming acquisition offers.',
    templateContent: '',
    requiredVariables: ['brandEntity', 'brandName', 'ceoName', 'stateOfFormation'],
    generationPrompt:
      'Generate Letter of Intent template for incoming acquisition of {{brandEntity}} (d/b/a {{brandName}}). Structure: 1) Parties (Acquirer TBD, Target: {{brandEntity}}), 2) Transaction structure (asset purchase vs equity purchase), 3) Purchase price and consideration, 4) IP license implications ({{brandName}} IP held by trust — license transfers or terminates per license agreement), 5) Key employees and retention, 6) Representations and warranties, 7) Due diligence period and scope, 8) Exclusivity period, 9) Conditions to closing, 10) Governing law ({{stateOfFormation}}), 11) Non-binding nature (except exclusivity and confidentiality). Signed by {{ceoName}} (CEO). Format: LOI template with fillable terms.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 4,
    sortOrder: 7,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },
];
